import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
    Container, Paper, Typography, Button, Alert, CircularProgress, Box,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
    Link, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Chip, Tooltip
} from '@mui/material';
import { EditNote as EditNoteIcon, StickyNote2 as NoteIcon } from '@mui/icons-material';
import NavigationBar from '../components/NavigationBar';
import { apiFetch } from '../api/apiFetch';
import { exportDatasetCsv } from '../utils/exportCsv';
import { isTierGateStatus, tierMessage } from '../api/tierError';

// Split a comma-separated tag string into trimmed, non-empty tags.
const parseTags = (tags) => String(tags || '').split(',').map((t) => t.trim()).filter(Boolean);

// AUTH-7: in-page premium action button. The whole /history page is already
// paid-gated by TierGate, so every viewer is entitled — a plain action button.
// The interim passcode wall (PremiumButton) was retired at the Phase C cutover.
function HistoryActionButton({ onAction, children, ...props }) {
    return <Button onClick={onAction} {...props}>{children}</Button>;
}

function HistoryPage() {
    const navigate = useNavigate();
    const [datasets, setDatasets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({ total: 0, limit: 50, offset: 0, hasMore: false });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    // #8 — premium notes/tags annotation editor.
    const [editRow, setEditRow] = useState(null);
    const [editNotes, setEditNotes] = useState('');
    const [editTags, setEditTags] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    useEffect(() => {
        fetchDatasets(rowsPerPage, page * rowsPerPage);
    }, [page, rowsPerPage]);

    async function fetchDatasets(limit = 50, offset = 0) {
        try {
            setLoading(true);
            setError(null);
            const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
            const res = await apiFetch(`${backendUrl}/api/datasets?limit=${limit}&offset=${offset}`, {
                credentials: 'include'
            });

            // AUTH-13: a tier-gate denial (401/402/403) → an upgrade/sign-in prompt
            // rather than a generic error. Normally unreachable (TierGate walls this
            // page when auth is on), but covers a session/tier change mid-flight.
            if (isTierGateStatus(res.status)) {
                let body = {};
                try { body = await res.json(); } catch { /* non-JSON body */ }
                const tm = tierMessage(res.status, body);
                setError(tm ? tm.text : 'Access denied.');
                setLoading(false);
                return;
            }

            if (!res.ok) {
                throw new Error(`Failed to fetch datasets: ${res.statusText}`);
            }

            const data = await res.json();

            // Handle pagination response format
            const datasetsArray = data.datasets || data;
            const paginationData = data.pagination || { total: datasetsArray.length, limit, offset, hasMore: false };

            const parsedData = datasetsArray.map(d => ({
                ...d,
                ad_value: parseFloat(d.ad_value),
                p_value: parseFloat(d.p_value)
            }));

            setDatasets(parsedData);
            setPagination(paginationData);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching datasets:', error);
            setError('Failed to load previous submissions. Please refresh the page.');
            setLoading(false);
        }
    }

    const handleLoadDataset = (dataset) => {
        // Navigate to cockpit with the dataset in state
        navigate('/', { state: { dataset } });
    };

    // #8 — open the notes/tags editor for a row (page is already paid-gated by TierGate).
    const openAnnotate = (dataset) => {
        setEditRow(dataset);
        setEditNotes(dataset.notes || '');
        setEditTags(dataset.tags || '');
        setSaveError(null);
    };

    const saveAnnotation = async () => {
        if (!editRow) return;
        try {
            setSaving(true);
            setSaveError(null);
            const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
            const res = await apiFetch(`${backendUrl}/api/datasets/${editRow.id}`, {
                method: 'PATCH',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ notes: editNotes, tags: editTags }),
            });
            if (isTierGateStatus(res.status)) {
                let body = {};
                try { body = await res.json(); } catch { /* non-JSON body */ }
                const tm = tierMessage(res.status, body);
                setSaveError(tm ? tm.text : 'Access denied.');
                setSaving(false);
                return;
            }
            if (!res.ok) throw new Error(`Save failed (${res.status})`);
            const updated = await res.json();
            setDatasets((prev) => prev.map((d) => (d.id === editRow.id ? { ...d, notes: updated.notes, tags: updated.tags } : d)));
            setEditRow(null);
        } catch (e) {
            setSaveError('Could not save. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
            <NavigationBar />
        <Container
            maxWidth="lg"
            sx={{
                padding: { xs: '1rem', sm: '2rem' },
            }}
        >
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" component="h1">
                    Submission History
                </Typography>
            </Box>

            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                View and reload your previous capability analyses. Click any row to load the dataset into the cockpit.
            </Typography>

            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                {/* Error Display */}
                {error && (
                    <Alert severity="error" onClose={() => setError(null)} sx={{ m: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Loading State */}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
                        <CircularProgress />
                    </Box>
                )}

                {/* Table */}
                {!loading && datasets.length > 0 && (
                    <>
                        <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }} align="right">USL</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }} align="right">LSL</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }} align="right">Mean</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }} align="right">Std Dev</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }} align="right">Cp</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }} align="right">Cpk</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }} align="center">Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {datasets.map((dataset) => (
                                        <TableRow
                                            key={dataset.id}
                                            hover
                                            sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                                            onClick={() => handleLoadDataset(dataset)}
                                        >
                                            <TableCell>{dataset.id}</TableCell>
                                            <TableCell sx={{ maxWidth: 240 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {dataset.description}
                                                    </Box>
                                                    {dataset.notes && (
                                                        <Tooltip title={dataset.notes}>
                                                            <NoteIcon sx={{ fontSize: 15, color: 'rgba(255,213,79,0.85)', flexShrink: 0 }} />
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                                {parseTags(dataset.tags).length > 0 && (
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, mt: 0.4 }}>
                                                        {parseTags(dataset.tags).map((t) => (
                                                            <Chip key={t} label={t} size="small" sx={{ height: 16, fontSize: '0.6rem', backgroundColor: 'rgba(144,202,249,0.15)', color: '#90caf9' }} />
                                                        ))}
                                                    </Box>
                                                )}
                                            </TableCell>
                                            <TableCell align="right">{dataset.usl?.toFixed(2)}</TableCell>
                                            <TableCell align="right">{dataset.lsl?.toFixed(2)}</TableCell>
                                            <TableCell align="right">{dataset.mean?.toFixed(3)}</TableCell>
                                            <TableCell align="right">{dataset.std_dev?.toFixed(4)}</TableCell>
                                            <TableCell align="right">{dataset.cp?.toFixed(2)}</TableCell>
                                            <TableCell align="right">{dataset.cpk?.toFixed(2)}</TableCell>
                                            <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                                <Box sx={{ display: 'inline-flex', gap: 1 }}>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() => handleLoadDataset(dataset)}
                                                    >
                                                        Load
                                                    </Button>
                                                    {/* CSV export from History is a premium feature (#6). */}
                                                    <HistoryActionButton
                                                        onAction={() => exportDatasetCsv(dataset)}
                                                        size="small"
                                                        variant="outlined"
                                                        aria-label={`Export dataset ${dataset.id} as CSV`}
                                                        sx={{ color: '#ffd54f', borderColor: 'rgba(255,213,79,0.4)', '&:hover': { borderColor: '#ffd54f', backgroundColor: 'rgba(255,213,79,0.08)' } }}
                                                    >
                                                        CSV
                                                    </HistoryActionButton>
                                                    {/* Notes / tags annotation (#8, premium). */}
                                                    <HistoryActionButton
                                                        onAction={() => openAnnotate(dataset)}
                                                        size="small"
                                                        variant="outlined"
                                                        startIcon={<EditNoteIcon />}
                                                        aria-label={`Edit notes and tags for dataset ${dataset.id}`}
                                                        sx={{ color: '#ffd54f', borderColor: 'rgba(255,213,79,0.4)', '&:hover': { borderColor: '#ffd54f', backgroundColor: 'rgba(255,213,79,0.08)' } }}
                                                    >
                                                        Notes
                                                    </HistoryActionButton>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            component="div"
                            count={pagination.total}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </>
                )}

                {/* Empty State */}
                {!loading && datasets.length === 0 && !error && (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
                            No previous submissions yet
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                            Submit your first dataset in the cockpit to see it here.
                        </Typography>
                        <Button
                            component={RouterLink}
                            to="/"
                            variant="contained"
                            color="primary"
                        >
                            Go to Cockpit
                        </Button>
                    </Box>
                )}
            </Paper>

            {/* Footer */}
            <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Submissions are stored per browser session. Clearing browser data will remove access.{' '}
                    <Link component={RouterLink} to="/documentation#data-privacy">
                        Learn more
                    </Link>
                </Typography>
            </Box>

            {/* Notes / tags editor (#8, premium) */}
            <Dialog open={!!editRow} onClose={() => !saving && setEditRow(null)} maxWidth="sm" fullWidth
                PaperProps={{ sx: { backgroundColor: '#1a1f26', border: '1px solid rgba(255,255,255,0.12)' } }}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'rgba(255,255,255,0.9)' }}>
                    <EditNoteIcon sx={{ color: '#ffd54f' }} /> Notes &amp; tags
                    {editRow && (
                        <Typography component="span" variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', ml: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            #{editRow.id} — {editRow.description}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent>
                    {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
                    <TextField
                        label="Notes" multiline minRows={3} fullWidth value={editNotes}
                        onChange={(e) => setEditNotes(e.target.value)} inputProps={{ maxLength: 2000 }}
                        placeholder="Observations, corrective actions, context…"
                        sx={{ mt: 1, mb: 2 }}
                    />
                    <TextField
                        label="Tags (comma-separated)" fullWidth value={editTags}
                        onChange={(e) => setEditTags(e.target.value)} inputProps={{ maxLength: 500 }}
                        placeholder="line-3, q3, recalibrated"
                    />
                    {parseTags(editTags).length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                            {parseTags(editTags).map((t) => (
                                <Chip key={t} label={t} size="small" sx={{ backgroundColor: 'rgba(144,202,249,0.15)', color: '#90caf9' }} />
                            ))}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setEditRow(null)} disabled={saving} sx={{ color: 'rgba(255,255,255,0.6)' }}>Cancel</Button>
                    <Button onClick={saveAnnotation} variant="contained" disabled={saving}>
                        {saving ? 'Saving…' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
        </Box>
    );
}

export default HistoryPage;
